import React, { useEffect, useState } from "react";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import PlantApi from "../../services/PlantApi";
import CompanyAPI from "../../services/CompanyApi";
import IApiPlantsResponse from "../../interfaces/IApiPlantsResponse";
import { useToast } from "../../context/ToastContext";

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListPlants_filters";

const ListPlants: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { t: translateMessages } = useTranslation("messages");

  const [filteredPlants, setFilteredPlants] = useState<IApiPlantsResponse[]>([]);
  const [initialData, setInitialData] = useState<IApiPlantsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters state
  const [filterName, setFilterName] = useState("");
  const [filterCompanyIds, setFilterCompanyIds] = useState<string[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Pagination/Sort state (Client-side)
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    fetchPlants();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (initialData.length > 0) {
      if (savedFilters.name) setFilterName(savedFilters.name.value);
      if (savedFilters.companyIds) setFilterCompanyIds(savedFilters.companyIds.value || []);
      applyFilters(savedFilters);
    }
  }, [initialData]);

  const fetchPlants = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await PlantApi.GetAllPlants();
      const fetchedPlants = response.data || [];
      setInitialData(fetchedPlants);
      setFilteredPlants(fetchedPlants);
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async (): Promise<void> => {
    try {
      const response = await CompanyAPI.GetAllCompanies();
      const fetched = response.data || [];
      setCompanies(fetched.map((c: any) => ({
        id: c.id,
        label: c.name,
        description: c.name,
        value: c.id
      })));
    } catch (error) {
      console.error(error);
    }
  };

  const applyFilters = (filters: Record<string, any>): void => {
    let filtered = [...initialData];

    if (filters.name?.value) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(filters.name.value.toLowerCase())
      );
    }

    if (filters.companyIds?.value?.length > 0) {
      filtered = filtered.filter((p) =>
        filters.companyIds.value.includes(p.company?.id)
      );
    }

    setFilteredPlants(filtered);
    setPage(0);
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      companyIds: filterCompanyIds.length > 0 ? { value: filterCompanyIds } : undefined,
    };

    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    applyFilters(filters);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterCompanyIds([]);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    setFilteredPlants(initialData);
    setPage(0);
  };

  const deletePlant = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await PlantApi.DeletePlant(id);
      displayToast("success", translateMessages("editSuccess"), "", false, 3000);
      fetchPlants();
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (plant: IApiPlantsResponse): void => {
    confirmDialog({
      message: translateMessages("deletePlant"),
      header: translateGeneral("confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: translateForm("Yes"),
      rejectLabel: translateForm("No"),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => deletePlant(plant.id),
    });
  };

  const handlePageChange = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
  };

  const handleSortChange = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const actionBodyTemplate = (rowData: IApiPlantsResponse): JSX.Element => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <IconButton
        icon="visibility"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/plants/${rowData.id}`)}
        aria-label={translateGeneral("seeDetails")}
      />
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/plants/${rowData.id}/edit`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData)}
        aria-label={translateGeneral("delete")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("plant")}
        description="Gerencie as unidades fabris do sistema"
      >
        <Button
          label={translateGeneral("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/plants/create")}
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title="Filtros"
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={translateForm("name")}
            placeholder="Pesquisar por nome"
            value={filterName}
            onChange={(val: any) => setFilterName(val)}
          />
          <Input
            type="multiselect"
            label={translateForm("company")}
            placeholder="Selecione as empresas"
            value={filterCompanyIds}
            options={companies}
            onChange={(val: any) => setFilterCompanyIds(val)}
          />
        </FilterPanel>

        <DataTable
          value={filteredPlants}
          paginated
          first={page}
          rows={rows}
          totalRecords={filteredPlants.length}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[10, 20, 50]}
          loading={isLoading}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          onSort={handleSortChange}
          emptyMessage={translateGeneral("noRecordsFound")}
        >
          <Column field="name" header={translateForm("name")} sortable />
          <Column field="company.name" header={translateForm("company")} sortable />
          <Column
            header={translateForm("actions")}
            body={actionBodyTemplate}
            style={{ width: "8rem" }}
          />
        </DataTable>
      </div>
      <ConfirmDialog />
    </div>
  );
};

export default ListPlants;
